import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../api/nocode';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import SafeIcon from '../../common/SafeIcon';
import { Link, useLocation } from 'react-router-dom';
import { getCampStatus } from '../../utils/campLogic';

export default function Dashboard() {
  const { user, setUser } = useAuth();
  const location = useLocation();
  const runSlug = location.pathname.split('/')[2] || '';
  const lettersPath = runSlug ? `/hub/${runSlug}/letters` : '/letters';
  const upgradePath = runSlug ? `/hub/${runSlug}/upgrade` : '/upgrade';

  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [nextBlockTime, setNextBlockTime] = useState(null);
  const [timeLeft, setTimeLeft] = useState('');

  // Lab Partner Modal States
  const [showBunkmateModal, setShowBunkmateModal] = useState(false);
  const [updatingBunkmate, setUpdatingBunkmate] = useState(false);

  const { isAAP, isEvergreen, currentTrailDay, isPostCamp, windowOpen } = getCampStatus(user, settings);
  const isLockedOut = !windowOpen;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [resSettings, resContent] = await Promise.all([
          api.get('settings_gwaacademy'),
          api.get('academy_content_gwaacademy')
        ]);

        const dataSettings = Array.isArray(resSettings) ? resSettings : resSettings.data || [];
        const map = {};
        dataSettings.forEach(s => {
          if (s.setting_key) map[s.setting_key] = s.value;
        });
        setSettings(map);

        const contentData = Array.isArray(resContent) ? resContent : resContent.data || [];
        const nowTime = new Date();
        
        let upcomingTimes = [];
        
        // Modules explicitly tagged with unlock_time
        contentData.forEach(c => {
          const isActive = c.is_active === 1 || c.is_active === true || c.is_active === '1';
          if (c.unlock_time && isActive) {
            const t = new Date(c.unlock_time);
            if (t > nowTime) upcomingTimes.push(t);
          }
        });

        // Day boundaries based on trail_start_date or camp_start
        const passType = user?.pass_type || 'free';
        const isUserEvergreen = passType === 'evergreen' || passType === 'evergreen-aap';

        if (isUserEvergreen && user?.trail_start_date) {
          const [y, m, d] = user.trail_start_date.split('-');
          const startDate = new Date(y, m - 1, d, 7, 0, 0); // 7 AM local time
          for (let i = 1; i <= 5; i++) {
            const dayUnlockTime = new Date(startDate.getTime());
            dayUnlockTime.setDate(startDate.getDate() + (i - 1));
            if (dayUnlockTime > nowTime) upcomingTimes.push(dayUnlockTime);
          }
        } else if (map.camp_start) {
          const startDate = new Date(`${map.camp_start}T07:00:00-04:00`);
          for (let i = 1; i <= 5; i++) {
            const dayUnlockTime = new Date(startDate.getTime());
            dayUnlockTime.setDate(startDate.getDate() + (i - 1));
            if (dayUnlockTime > nowTime) upcomingTimes.push(dayUnlockTime);
          }
        }

        if (upcomingTimes.length > 0) {
          upcomingTimes.sort((a, b) => a - b);
          setNextBlockTime(upcomingTimes[0]);
        } else {
          setNextBlockTime(null);
        }
      } catch (e) {
        console.error("Failed to load dashboard data", e);
      } finally {
        setLoading(false);
      }
    };
    if (user) fetchData();
  }, [user]);

  useEffect(() => {
    if (!nextBlockTime) return;
    const calculate = () => {
      const now = new Date().getTime();
      const distance = nextBlockTime.getTime() - now;
      if (distance < 0) {
         setTimeLeft('Unlocked!');
         return false;
      }
      const days = Math.floor(distance / (1000 * 60 * 60 * 24));
      const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60));
      const minutes = Math.floor((distance % (1000 * 60)) / (1000 * 60));
      const seconds = Math.floor((distance % (1000 * 60)) / 1000);
      if (days > 0) {
         setTimeLeft(`${days}d ${hours.toString().padStart(2, '0')}h ${minutes.toString().padStart(2, '0')}m`);
      } else {
         setTimeLeft(`${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`);
      }
      return true;
    };
    calculate();
    const interval = setInterval(() => { if (!calculate()) clearInterval(interval); }, 1000);
    return () => clearInterval(interval);
  }, [nextBlockTime]);

  useEffect(() => {
    if (user && isAAP && user.wants_bunkmate == null) {
      setShowBunkmateModal(true);
    }
  }, [user, isAAP]);

  const handleBunkmateChoice = async (choice) => {
    setUpdatingBunkmate(true);
    try {
      await api.put('students_gwaacademy', user.id, { wants_bunkmate: choice });
      setUser({ ...user, wants_bunkmate: choice });
      setShowBunkmateModal(false);
    } catch (err) {
      console.error("Failed to update lab partner preference", err);
      alert("Failed to save your choice. Please try again.");
    } finally {
      setUpdatingBunkmate(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8 text-center text-muted flex flex-col items-center justify-center min-h-[400px]">
        <SafeIcon name="Loader" className="animate-spin text-4xl text-amber mb-4" />
        <p>Loading your academy experience...</p>
      </div>
    );
  }

  if (isLockedOut) {
    return (
      <div className="max-w-4xl mx-auto py-12 px-4 animate-fadeIn">
        <Card className="text-center py-16 px-8 border-2 border-forest-green shadow-xl bg-[#EAF1E6]">
          <div className="w-24 h-24 bg-forest-green rounded-full flex items-center justify-center mx-auto mb-6 shadow-md text-white">
            <SafeIcon name="Compass" className="text-5xl" />
          </div>
          <h1 className="text-4xl font-bold text-dark-green mb-4 font-poppins">Academy is Officially Wrapped!</h1>
          <p className="text-lg text-forest-green font-medium mb-6 max-w-2xl mx-auto">
            Thank you for joining us at GospelWise Author Academy, {user?.name?.split(' ')[0]}. We hope you found clarity and direction for your calling.
          </p>
          <div className="max-w-2xl mx-auto bg-white p-6 rounded-md shadow-sm border border-tan mb-8 space-y-4">
            <p className="text-muted leading-relaxed whitespace-pre-wrap">
              {settings.replay_expiry_msg || "Your Free Pass replay access has now expired, but your journey is just beginning. Make sure to download your completed Compass workbook to guide your next steps."}
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto text-left">
            <Card className="bg-gwa-blue text-white border-none shadow-md">
              <h3 className="font-bold text-xl mb-2 flex items-center gap-2">
                <SafeIcon name="Star" className="text-amber" /> Fellowship+
              </h3>
              <p className="text-sm opacity-90 mb-4 leading-relaxed">
                Don't write alone. Join the Fellowship+ community as a founding member to get ongoing coaching, community, and craft workshops.
              </p>
              <a href="https://gospelwiseauthor.com/fellowship" target="_blank" rel="noopener noreferrer">
                <Button className="w-full bg-white text-gwa-blue hover:bg-gray-100 font-bold">Become a Founding Member</Button>
              </a>
            </Card>
            <Card className="bg-amber/10 border-amber shadow-sm">
              <h3 className="font-bold text-dark-green text-xl mb-2 flex items-center gap-2">
                <SafeIcon name="Calendar" className="text-amber" /> Save the Date
              </h3>
              <p className="text-sm text-dark-green mb-4 leading-relaxed">
                We're doing this again! Mark your calendar for the big October GospelWise Author Academy challenge where we write our books together.
              </p>
              <div className="bg-white p-3 text-center rounded text-amber font-bold border border-amber/30 text-lg">
                OCTOBER 1 - 31, 2026
              </div>
            </Card>
          </div>
        </Card>
      </div>
    );
  }

  const runBase = runSlug ? `/hub/${runSlug}` : '/hub';
  const schedulePath = (path) => `${runBase}${path}${location.search}`;

  const schedule = [
    { time: '7:00 AM', title: 'Morning Lesson', path: schedulePath('/lesson'), icon: 'Edit' },
    { time: '12:30 PM', title: 'Midday Lab', path: schedulePath('/lab'), icon: 'Monitor' },
    { time: '2:00 PM', title: 'Study Hall', path: schedulePath('/studyhall'), icon: 'BookOpen' },
    { time: '7:00 PM', title: 'The Commons', path: schedulePath('/commons'), icon: 'Users' },
  ];

  const showSchedule = settings.show_todays_schedule === 'true' || isEvergreen;
  const showOrientation = settings.show_camp_orientation !== 'false';

  return (
    <div className="space-y-8 max-w-5xl mx-auto animate-fadeIn relative">
      {showBunkmateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 animate-fadeIn">
          <Card className="max-w-lg w-full p-8 text-center space-y-6 shadow-2xl border-t-8 border-amber relative">
            <div className="mx-auto w-16 h-16 bg-amber/20 rounded-full flex items-center justify-center text-3xl text-amber">
              <SafeIcon name="Users" />
            </div>
            <h2 className="text-2xl font-bold text-dark-green font-poppins">Get Paired with a Lab Partner!</h2>
            <p className="text-muted leading-relaxed">
              As an All-Access pass holder, you can be paired with a fellow Christian writer to make a real connection.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <Button onClick={() => handleBunkmateChoice(1)} disabled={updatingBunkmate} className="flex-1 bg-forest-green hover:bg-dark-green py-3 text-lg">
                {updatingBunkmate ? 'Saving...' : 'Yes, pair me up!'}
              </Button>
              <Button onClick={() => handleBunkmateChoice(0)} disabled={updatingBunkmate} variant="outline" className="flex-1 border-tan text-muted hover:text-dark-green py-3 text-lg">
                No, I'll write solo
              </Button>
            </div>
          </Card>
        </div>
      )}

      <header className="flex justify-between items-end border-b-2 border-tan pb-4">
        <div>
          <h1 className="text-3xl font-bold text-dark-green">Welcome back, {user?.name?.split(' ')[0]}!</h1>
          <p className="text-muted mt-1">
            {isPostCamp ? 'Academy Archive • ' : (currentTrailDay === 0 ? 'Academy Orientation • ' : `Day ${currentTrailDay} of 5 • `)}
            {isAAP ? 'All-Access Pass' : 'Free Pass'}
          </p>
        </div>
        {!isPostCamp && currentTrailDay > 0 && (
          <div className="bg-forest-green text-white px-4 py-2 rounded-md font-bold text-lg hidden sm:block">
            Day {currentTrailDay}
          </div>
        )}
      </header>

      {isPostCamp && (
        <div className="relative overflow-hidden rounded-xl bg-gradient-to-r from-gwa-blue to-blue-900 text-white shadow-lg mb-8 border-b-4 border-amber">
          <div className="absolute top-0 right-0 p-4 opacity-10 pointer-events-none">
            <SafeIcon name="Award" className="text-[140px]" />
          </div>
          <div className="p-8 md:p-10 relative z-10">
            <div className="flex flex-col md:flex-row items-center gap-8">
              <div className="flex-1 space-y-4 text-center md:text-left">
                <div className="inline-flex items-center gap-2 bg-amber/20 text-amber px-3 py-1 rounded-full text-sm font-bold tracking-wide uppercase">
                  <SafeIcon name="Award" /> Academy Wrap-Up
                </div>
                <h3 className="text-3xl lg:text-4xl font-bold font-poppins leading-tight">
                  Academy is officially wrapped{isAAP ? ', but your access is lifetime.' : '!'}
                </h3>
                <p className="text-lg lg:text-xl text-blue-50 opacity-95 leading-relaxed whitespace-pre-wrap max-w-3xl">
                  {settings.camp_closed_msg 
                    ? settings.camp_closed_msg 
                    : (isAAP 
                      ? settings.aa_welcome_msg || "As an All-Access pass holder, you retain full access to all video replays, the Compass workbook, and your personal letters. Take your time and revisit any session you need."
                      : "You currently have access to all replays and the Compass workbook until your replay access expires. Make sure to download your Compass before then!")}
                </p>
              </div>
              <div className="w-full md:w-auto shrink-0">
                <a href="https://gospelwiseauthor.com/shop" target="_blank" rel="noopener noreferrer">
                  <Button className="w-full md:w-auto bg-orange text-gwa-blue hover:bg-gray-100 font-bold px-8 py-4 text-lg shadow-xl hover:-translate-y-0.5 transition-all">
                    Explore More Resources
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="md:col-span-2 space-y-6 flex flex-col">
          {(showSchedule || isPostCamp) && (
            <Card className={`bg-white border-l-4 ${isPostCamp ? 'border-l-forest-green' : 'border-l-amber'}`}>
              <h2 className="text-xl font-bold text-dark-green mb-4 flex items-center gap-2">
                <SafeIcon name={isPostCamp ? "Archive" : "Calendar"} /> {isPostCamp ? 'Academy Archive' : "Today's Schedule"}
              </h2>
              <div className="space-y-4">
                {schedule.map((item, idx) => (
                  <Link to={item.path} key={idx} className="block hover:bg-cream rounded-md transition-colors p-2 -mx-2">
                    <div className="flex items-center gap-4">
                      {!isPostCamp && <div className="w-24 text-sm font-semibold text-forest-green">{item.time}</div>}
                      <div className="flex-1 flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-tan flex items-center justify-center text-dark-green">
                          <SafeIcon name={item.icon} />
                        </div>
                        <span className="font-medium text-dark-green">{item.title}</span>
                      </div>
                      <SafeIcon name="ChevronRight" className="text-muted" />
                    </div>
                  </Link>
                ))}
              </div>
            </Card>
          )}

          {showOrientation && (!isPostCamp || currentTrailDay === 0) && (
            <Card className="bg-[#fdfaf6] border-2 border-tan shadow-sm relative overflow-hidden">
              <SafeIcon name="Map" className="absolute -right-10 -bottom-10 text-[180px] text-tan opacity-20 pointer-events-none" />
              <div className="relative z-10">
                <h2 className="text-2xl font-bold text-dark-green mb-6 flex items-center gap-2">
                  <SafeIcon name="Compass" className="text-forest-green" /> Academy Orientation
                </h2>
                {settings.orientation_video_url && (
                  <div className="mb-6 rounded-md overflow-hidden shadow-md aspect-video bg-black">
                    <iframe src={settings.orientation_video_url} className="w-full h-full border-0" allowFullScreen></iframe>
                  </div>
                )}
                {settings.orientation_welcome_text && (
                  <div className="mb-6 text-dark-green font-medium text-[17px] leading-relaxed whitespace-pre-wrap pb-6 border-b border-tan/50">
                    {settings.orientation_welcome_text}
                  </div>
                )}
                <div className="grid md:grid-cols-2 gap-4">
                  {isAAP ? (
                    <Link to={lettersPath} className="bg-white p-4 rounded-md border border-tan hover:border-amber flex items-start gap-4">
                      <div className="bg-amber/10 p-2 rounded-full text-amber shrink-0"><SafeIcon name="Mail" /></div>
                      <div>
                        <h4 className="font-bold text-dark-green">Write Your Letters</h4>
                        <p className="text-sm text-muted mt-1 leading-tight">Schedule your 5 daily self-letters before academy begins.</p>
                      </div>
                    </Link>
                  ) : (
                    <Link to={upgradePath} className="bg-gray-50 p-4 rounded-md border border-gray-200 hover:border-amber flex items-start gap-4 opacity-75 group">
                      <div className="bg-gray-200 p-2 rounded-full text-gray-500 group-hover:text-amber shrink-0"><SafeIcon name="Lock" /></div>
                      <div>
                        <h4 className="font-bold text-gray-700 group-hover:text-dark-green flex items-center gap-1.5">
                          Write Your Letters <span className="text-[10px] uppercase font-bold tracking-wider text-amber bg-amber/10 px-1.5 py-0.5 rounded">All-Access</span>
                        </h4>
                        <p className="text-sm text-muted mt-1 leading-tight">Upgrade to All-Access to write and schedule your daily self-letters.</p>
                      </div>
                    </Link>
                  )}
                  <a href={settings.global_fb_link || "#"} target="_blank" rel="noopener noreferrer" className="bg-white p-4 rounded-md border border-tan hover:border-gwa-blue flex items-start gap-4">
                    <div className="bg-gwa-blue/10 p-2 rounded-full text-gwa-blue shrink-0"><SafeIcon name="Users" /></div>
                    <div>
                      <h4 className="font-bold text-dark-green">Facebook Group</h4>
                      <p className="text-sm text-muted mt-1 leading-tight">Introduce yourself and meet your fellow campers.</p>
                    </div>
                  </a>
                </div>
              </div>
            </Card>
          )}

          <Card className="bg-[#EAF1E6] border-none">
            <h3 className="font-bold text-forest-green mb-2">Director's Note</h3>
            <p className="text-base text-dark-green leading-relaxed whitespace-pre-wrap">
              {settings.welcome_message || (isPostCamp 
                ? `"The hardest part of writing is continuing to show up after the momentum of academy fades. Keep your Compass close, and take the next faithful step."` 
                : `"The hardest part of writing is showing up. Let's get to work!"`)}
            </p>
          </Card>
        </div>

        <div className="space-y-6">
          {!isPostCamp && (
            <Card className="text-center py-8">
              {nextBlockTime ? (
                <>
                  <div className="text-4xl font-bold text-gwa-blue mb-2">{timeLeft || '...'}</div>
                  <p className="text-sm text-muted uppercase tracking-wider font-semibold">Until Next Block</p>
                </>
              ) : (
                <div className="text-2xl font-bold text-gwa-blue">Academy is Live!</div>
              )}
            </Card>
          )}

          <Card className="bg-gradient-to-br from-gwa-blue to-blue-800 text-white border-none">
            <h3 className="font-bold mb-2 flex items-center gap-2">
              <SafeIcon name="Star" /> All-Access Perks
            </h3>
            <ul className="text-sm space-y-2 opacity-90">
              <li>• Self-Letter writing is {isPostCamp ? 'archived' : 'open'}</li>
              <li>• Meet your Lab Partner</li>
              <li>• Lifetime replay access</li>
            </ul>
            {isAAP ? (
              <Link to={lettersPath} className="mt-4 block text-center bg-white text-gwa-blue py-2 rounded-md font-medium text-sm hover:bg-gray-100 transition">
                {isPostCamp ? 'Read Your Letters' : "Write Today's Letter"}
              </Link>
            ) : (
              <Link to={upgradePath} className="mt-4 block text-center bg-white/90 text-gwa-blue py-2 rounded-md font-medium text-sm hover:bg-white transition flex items-center justify-center gap-1.5">
                <SafeIcon name="Lock" className="text-xs" /> Upgrade to Write Today's Letter
              </Link>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}